import { useMemo, useState } from "react";
import { canonicalForm, type Verdict } from "@vocifuchs/shared";
import { practice } from "../copy/practice.js";

export interface MatchCard {
  term_id: string;
  direction: "ab" | "ba";
  prompt: string;
  solution: string;
  lang_prompt: string;
  lang_answer: string;
}

export interface MatchAnswer {
  term_id: string;
  direction: "ab" | "ba";
  verdict: Verdict;
  attempt: number;
}

interface Tile {
  id: number;
  label: string;
  lang: string;
}

/** Fisher-Yates. The board must not show the pairs in the same order twice. */
function shuffle<T>(items: T[]): T[] {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const tmp = copy[i] as T;
    copy[i] = copy[j] as T;
    copy[j] = tmp;
  }
  return copy;
}

const CLEAR_AFTER_WRONG_MS = 700;
/** The schema accepts at most five attempts per card. */
const MAX_ATTEMPT = 5;

/**
 * Five pairs, matched by tapping twice: first the word, then its partner. No
 * dragging — on a school iPad it is fiddly, and with a keyboard or a screen
 * reader it cannot be done at all (docs/UX.md §4 and §8).
 *
 * Every attempt is reported, right or wrong, so the round records the same
 * kind of results as the card flow. The cap that keeps a matching round from
 * carrying a word past box 1 lives in applyAnswer, not here.
 */
export function MatchBoard({
  cards,
  onAnswer,
  onDone,
}: {
  cards: MatchCard[];
  /** Each recorded attempt, right away: a cancel mid-board must not lose them. */
  onAnswer: (answer: MatchAnswer) => void;
  onDone: () => void;
}) {
  const tiles = useMemo<Tile[]>(
    () =>
      shuffle(
        cards.map((card, id) => ({ id, label: canonicalForm(card.solution), lang: card.lang_answer })),
      ),
    [cards],
  );

  const [pickedCard, setPickedCard] = useState<number | null>(null);
  const [pickedTile, setPickedTile] = useState<number | null>(null);
  const [solved, setSolved] = useState<number[]>([]);
  const [wrong, setWrong] = useState(false);
  const [attempts, setAttempts] = useState<Record<number, number>>({});
  const [live, setLive] = useState("");

  const judge = (cardIndex: number, tileId: number) => {
    const card = cards[cardIndex];
    const tile = tiles.find((t) => t.id === tileId);
    if (!card || !tile) return;
    // Show both halves as picked, whichever one was tapped first, so a wrong
    // pair turns red on both sides.
    setPickedCard(cardIndex);
    setPickedTile(tileId);

    // An exact comparison, not checkAnswer: the tolerance for accents and one
    // typo exists for what a child types, and it would let two similar words
    // on the board pass for each other. Two cards with the same wording match
    // either way, which is what we want.
    const hit = tile.label === canonicalForm(card.solution);
    // Wrong taps are unlimited on the board, recorded ones are not: only the
    // first wrong per word, then the hit. The board is where new words are met
    // first (issue #63), and every recorded wrong is a lapse -- guessing on
    // five unknown words must not mark one as fragile before it was ever
    // learned. It also keeps a round far below what the worker accepts.
    const tries = (attempts[cardIndex] ?? 0) + 1;
    setAttempts({ ...attempts, [cardIndex]: tries });
    if (tries === 1 || hit) {
      onAnswer({
        term_id: card.term_id,
        direction: card.direction,
        verdict: hit ? "correct" : "wrong",
        attempt: Math.min(tries, MAX_ATTEMPT),
      });
    }

    if (!hit) {
      setWrong(true);
      setLive(practice.notYet);
      window.setTimeout(() => {
        setWrong(false);
        setPickedCard(null);
        setPickedTile(null);
      }, CLEAR_AFTER_WRONG_MS);
      return;
    }

    const nextSolved = [...solved, cardIndex];
    setSolved(nextSolved);
    setPickedCard(null);
    setPickedTile(null);
    setLive(practice.matched(card.prompt, tile.label));
    if (nextSolved.length === cards.length) onDone();
  };

  const pickCard = (cardIndex: number) => {
    if (wrong || solved.includes(cardIndex)) return;
    if (pickedTile !== null) {
      judge(cardIndex, pickedTile);
      return;
    }
    setPickedCard(pickedCard === cardIndex ? null : cardIndex);
  };

  const pickTile = (tileId: number) => {
    if (wrong || solved.includes(tileId)) return;
    if (pickedCard !== null) {
      judge(pickedCard, tileId);
      return;
    }
    setPickedTile(pickedTile === tileId ? null : tileId);
  };

  const column =
    "min-h-14 rounded-2xl border-2 px-3 py-2.5 text-left text-lg font-semibold break-words transition-colors";
  const resting = "border-line bg-paper-2 hover:border-fox hover:bg-fox/5";
  const chosen = "border-fox bg-fox/10";
  const missed = "border-rose bg-rose/10 text-rose";

  return (
    <div className="space-y-4">
      <p className="text-ink-soft">{practice.matchHint}</p>

      {solved.length > 0 ? (
        <ul className="space-y-2">
          {solved.map((cardIndex) => {
            const card = cards[cardIndex];
            if (!card) return null;
            return (
              <li
                key={card.term_id + card.direction}
                className="flex items-center gap-2 rounded-2xl border border-moss/30 bg-moss/10 px-3 py-2 text-moss"
              >
                <span lang={card.lang_prompt} className="font-semibold">
                  {card.prompt}
                </span>
                <span aria-hidden>·</span>
                <span lang={card.lang_answer}>{canonicalForm(card.solution)}</span>
              </li>
            );
          })}
        </ul>
      ) : null}

      <div className="grid grid-cols-2 gap-3">
        <ul className="space-y-3">
          {cards.map((card, cardIndex) =>
            solved.includes(cardIndex) ? null : (
              <li key={card.term_id + card.direction}>
                <button
                  type="button"
                  lang={card.lang_prompt}
                  aria-pressed={pickedCard === cardIndex}
                  className={`w-full ${column} ${
                    wrong && pickedCard === cardIndex
                      ? missed
                      : pickedCard === cardIndex
                        ? chosen
                        : resting
                  }`}
                  onClick={() => pickCard(cardIndex)}
                >
                  {card.prompt}
                </button>
              </li>
            ),
          )}
        </ul>
        <ul className="space-y-3">
          {tiles.map((tile) =>
            solved.includes(tile.id) ? null : (
              <li key={tile.id}>
                <button
                  type="button"
                  lang={tile.lang}
                  aria-pressed={pickedTile === tile.id}
                  className={`w-full ${column} ${
                    wrong && pickedTile === tile.id ? missed : pickedTile === tile.id ? chosen : resting
                  }`}
                  onClick={() => pickTile(tile.id)}
                >
                  {tile.label}
                </button>
              </li>
            ),
          )}
        </ul>
      </div>

      <p aria-live="polite" className="sr-only">
        {live}
      </p>
    </div>
  );
}
